import {
  useCallback,
  useState,
  useEffect,
} from "react";
import {
  StatusFilterType,
  DrawerFilters,
} from "@pages/Dashboard/contexts/Filter.context";

interface UseFiltersDrawerProps {
  drawerFilters: DrawerFilters;
  setDrawerFilters?: React.Dispatch<React.SetStateAction<DrawerFilters>>;
  refreshMap?: () => void;
  onClose?: () => void;
  open: boolean;
}

/**
 * Hook para gerenciar o estado dos filtros no drawer
 * Mantém uma cópia local dos filtros enquanto o usuário edita,
 * e só aplica as mudanças quando clicar em "Aplicar"
 */
export function useFiltersDrawer({
  drawerFilters,
  setDrawerFilters,
  refreshMap,
  onClose,
  open,
}: UseFiltersDrawerProps) {
  // Estado local: guarda os filtros que o usuário está editando
  // Só é aplicado ao contexto quando clicar em "Aplicar"
  const [localDrawerFilters, setLocalDrawerFilters] = useState<DrawerFilters>(drawerFilters);

  useEffect(() => {
    if (open) {
      setLocalDrawerFilters(drawerFilters);
    }
  }, [open]);

  /**
   * Quando o usuário marca/desmarca um status no checkbox
   * @param variant - O variant do status que foi marcado/desmarcado (ex: "not-started")
   * @param checked - true = adicionar, false = remover
   */
  const handleStatusChange = useCallback((variant: StatusFilterType, checked: boolean) => {
    setLocalDrawerFilters((filtrosAtuais) => {
      const novosFiltros = { ...filtrosAtuais };

      if (checked) {
        novosFiltros.status = [...filtrosAtuais.status, variant];
      } else {
        novosFiltros.status = filtrosAtuais.status.filter((v) => v !== variant);
      }

      return novosFiltros;
    });
  }, []);

  /**
   * Quando o usuário marca/desmarca um canal no checkbox
   * @param channel - O canal que foi marcado/desmarcado
   * @param checked - true = adicionar, false = remover
   */
  const handleChannelChange = useCallback((channel: string, checked: boolean) => {
    setLocalDrawerFilters((filtrosAtuais) => {
      const novosFiltros = { ...filtrosAtuais };

      if (checked) {
        // Adiciona o canal na lista
        novosFiltros.channels = [...filtrosAtuais.channels, channel];
      } else {
        // Remove o canal da lista
        novosFiltros.channels = filtrosAtuais.channels.filter((c) => c !== channel);
      }

      return novosFiltros;
    });
  }, []);

  /**
   * Salva os filtros editados e aplica no sistema
   * Passo 1: Salva os filtros locais no contexto global
   * Passo 2: Atualiza a visualização com os novos filtros
   * Passo 3: Fecha o drawer
   */
  const handleApply = useCallback(() => {
    // Salva os filtros no contexto global
    setDrawerFilters?.(localDrawerFilters);

    // Atualiza a visualização (refaz o cálculo dos dados)
    refreshMap?.();

    onClose?.();
  }, [localDrawerFilters, setDrawerFilters, refreshMap, onClose]);

  /**
   * Descarta as alterações e volta aos filtros originais
   * Passo 1: Restaura os filtros originais no estado local
   * Passo 2: Fecha o drawer
   */
  const handleCancel = useCallback(() => {
    // Volta aos filtros que estavam salvos antes de abrir o drawer
    setLocalDrawerFilters(drawerFilters);
    onClose?.();
  }, [drawerFilters, onClose]);

  /**
   * Remove todos os filtros e aplica imediatamente
   * Passo 1: Limpa os filtros locais
   * Passo 2: Salva os filtros vazios no contexto
   * Passo 3: Atualiza a visualização
   * Passo 4: Fecha o drawer
   */
  const handleClear = useCallback(() => {
    // Cria um objeto com listas vazias
    const filtrosVazios: DrawerFilters = { status: [], channels: [], critical: [] };

    // Atualiza o estado local
    setLocalDrawerFilters(filtrosVazios);

    // Salva no contexto global
    setDrawerFilters?.(filtrosVazios);

    refreshMap?.();

    onClose?.();
  }, [setDrawerFilters, refreshMap, onClose]);

  return {
    localDrawerFilters,
    handleStatusChange,
    handleChannelChange,
    handleApply,
    handleCancel,
    handleClear,
  };
}
